
import type { SummaryBarProps, ControlStripProps, ToastsProps, ActionBtnProps } from 
"../helpers/types";
import {
  Square, Pause, Scissors, Mic,
  VolumeX, Volume2, ScissorsLineDashed, Eye,
} from "lucide-react";

export function ActionBtn({
  cls,
  icon,
  label,
  action,
}: Readonly<ActionBtnProps>) {
  return (
    <button
      type="button"
      onClick={action}
      aria-label={label}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-opacity hover:opacity-80 ${cls}`}
    >
      {icon}
      {label}
    </button>
  );
}

export function SummaryBar({
  rooms,
  activeFilter,
  onFilterChange
}: Readonly<SummaryBarProps>) {
  const counts = { live: 0, paused: 0, flagged: 0, rec: 0, offline: 0 };

  rooms.forEach(r => {
    if (r.status in counts) {
      (counts as Record<string, number>)[r.status]++;
    }
  });

  const totalVw = rooms.reduce((a, r) => a + (r.vw || 0), 0);

  const items: [keyof typeof counts, string, string][] = [
    ["live", "#22C55E", "Live"],
    ["paused", "#F59E0B", "Paused"],
    ["flagged", "#EF4444", "Flagged"],
    ["rec", "#A78BFA", "Rec only"],
    ["offline", "#9CA3AF", "Offline"],
  ];

  return (
    <div className="flex flex-wrap items-center gap-2 mb-2.5">

      {items.map(([key, color, label]) =>
        counts[key] > 0 ? (
          <button
            key={key}
            type="button"
            onClick={() =>
              onFilterChange(activeFilter === key ? "all" : key)
            }
            className={`flex items-center gap-1.5 text-[11px]
    px-2.5 py-1 rounded-full border transition-all
    ${activeFilter === key
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 bg-white"
              }
    text-gray-500 hover:bg-gray-100`}
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: color }}
            />
            <span className="font-semibold text-gray-800">{counts[key]}</span>
            <span>{label}</span>
          </button>
        ) : null
      )}


{totalVw > 0 && (
      <div className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-400">
        <div className=" ml-auto flex   items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-gray-200 bg-white text-gray-500">
          <Eye size={12} className="text-gray-600" aria-hidden="true" />
          <span className="font-semibold text-gray-800">{totalVw}</span>
          <span>viewers</span>
    </div>
  </div>
)}
    </div>
  );
}
export function ControlStrip({
  room,
  onStop,
  muted,
  onToggleMute,
  onToast,
}: Readonly<ControlStripProps>) {
  if (!room) return null;
  return (
    <div className="flex items-center flex-wrap gap-2 mt-2 px-3.5 py-2.5 rounded-[10px] border border-blue-500 bg-white">
      <span className="text-xs font-medium text-gray-500 mr-1 shrink-0">
        {room.id} · {room.blk} — {room.cs}
      </span>
      <ActionBtn
        cls="bg-red-100 text-red-800"
        icon={<Square size={14} />}
        label="Stop"
        action={onStop}
      />
      <ActionBtn
        cls="bg-amber-100 text-amber-800"
        icon={<Pause size={14} />}
        label="Pause"
        action={() => onToast("Stream paused — viewers see freeze frame", "#B45309")}
      />
      <ActionBtn
        cls="bg-blue-50 text-blue-700"
        icon={<ScissorsLineDashed size={14} />}
        label="Mask region"
        action={() => onToast("Active region masked in stream", "#1A56DB")}
      />
      <ActionBtn
        cls="bg-violet-100 text-violet-800"
        icon={<Mic size={14} />}
        label="Mute audio"
        action={() => onToast("Audio muted for this segment", "#5B21B6")}
      />
      <ActionBtn
        cls="bg-gray-100 text-gray-700"
        icon={<Scissors size={14} />}
        label="Cut"
        action={() => onToast("Segment marked for cut — confirm in editor", "#374151")}
      />
      <div className="ml-auto" />
      <button
        type="button"
        onClick={onToggleMute}
        aria-label={muted ? "Unmute stream audio" : "Mute stream audio"}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
      >
        {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        {muted ? "Muted" : "Audio on"}
      </button>
    </div>
  );
}
export function Toasts({ toasts }: Readonly<ToastsProps>) {
  return (
    <div className="fixed bottom-4 right-4 flex flex-col gap-1.5  z-[99999]" aria-live="polite">
      {toasts.map(t => (
        <div
          key={t.id}
          className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-white border border-gray-200 text-[12px]"
          style={{ animation: "tin .22s ease-out" }}
        >
          <span style={{ color: t.col, fontSize: 14 }} aria-hidden="true">●</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}